import withStore, {
  TPresentable,
  TLoadingAwarePresenter
} from "../../core/presenter/withStore";
import Application from "../../Application";
import AddOrganisationCaseDecisionTypesInteractor from "../interactors/AddOrganisationCaseDecisionTypesInteractor";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";
import GetOrganisationCaseDecisionTypesInteractor from "../interactors/GetOrganisationCaseDecisionTypesInteractor";
import { IOrganisationIndexCategory } from "../model/OrganisationIndexCategory";

import GetCaseDecisionTypeIndexCategoriesInteractor from "../interactors/GetCaseDecisionTypeIndexCategoriesInteractor";
import AddCaseDecisionTypeIndexCategoriesInteractor from "../interactors/AddCaseDecisionTypeIndexCategoriesInteractor";
import DocumentTypeIndexCategory, {
  IDocumentTypeIndexCategory
} from "../../documentTypes/model/DocumentTypeIndexCategory";
import { IDocumentType } from "../../documentTypes/model/DocumentType";
import { IVisibilityLevel } from "../../documentTypes/model/VisibilityLevel";
import GetAllCaseDecisionTypesInteractor from "../../caseDecisions/interactor/GetAllCaseDecisionTypesInteractor";
import RemoveOrganisationCaseDecisionTypeInteractor from "../interactors/RemoveOrganisationCaseDecisionTypeInteractor";

export interface TOrganisationCaseDecisionTypesPresenter
  extends TLoadingAwarePresenter {
  organisationId: number;
  organisationCaseDecisionTypes: IDocumentType[];
  caseDecisionTypes: IDocumentType[];
  modalVisible: boolean;
  assignTypesToOrganisationModalVisible: boolean;
  currentCaseDecisionTypeId?: number;
  organisationIndexCategories: IOrganisationIndexCategory[];
  caseDecisionTypeIndexCategories: IDocumentTypeIndexCategory[];
  codeList: number[];
}

export interface IOrganisationCaseDecisionTypesPresenter
  extends TOrganisationCaseDecisionTypesPresenter,
    TPresentable {
  loadReferenceValues(
    organisationId: number,
    organisationIndexCategories: IOrganisationIndexCategory[],
    caseDecisionType: IDocumentType[],
    organisationCaseDecisionTypes: IDocumentType[]
  ): void;
  onChangeAddElementsModalVisible(value: boolean, itemId?: number): void;
  addCaseDecisionTypeToOrganisation(data: any): void;
  onChangeAddTypesToOrganisationModalVisible(value: boolean): void;
  getCaseDecisionTypesForOrganisation(): void;
  loadOrganisationCaseDecisionTypes(
    organisationCaseDecisionTypes: IDocumentType[],
    caseDecisionTypes: IDocumentType[]
  ): void;
  getCaseDecisionTypeIndexCategories(caseDecisionTypeId: number): void;
  addCaseDecisionTypeIndexCategories(items: any): void;
  setOrganisationCaseDecisionTypeData(id: number): void;
  removeOrganisationCaseLawDocumentType(id: number): void;
}

const defaultState: TOrganisationCaseDecisionTypesPresenter = {
  organisationId: NaN,
  organisationCaseDecisionTypes: [],
  modalVisible: false,
  currentCaseDecisionTypeId: undefined,
  codeList: [],
  caseDecisionTypes: [],
  assignTypesToOrganisationModalVisible: false,
  organisationIndexCategories: [],
  caseDecisionTypeIndexCategories: []
};

const OrganisationCaseDecisionTypesPresenter = withStore<
  IOrganisationCaseDecisionTypesPresenter,
  TOrganisationCaseDecisionTypesPresenter
>(
  ({
    application,
    store,
    loader,
    translate
  }): IOrganisationCaseDecisionTypesPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<
      TOrganisationCaseDecisionTypesPresenter & TLoadingAwarePresenter
    >();
    loader.start("organisationCaseDecisionType");
    const loadOrganisationCaseDecisionTypes = (
      organisationCaseDecisionTypes: IDocumentType[],
      caseDecisionTypes: IDocumentType[]
    ) => {
      _store.update({
        organisationCaseDecisionTypes,
        caseDecisionTypes: caseDecisionTypes.filter(
          (item) =>
            !organisationCaseDecisionTypes.map((el) => el.id).includes(item.id)
        )
      });
      loader.stop("organisationCaseDecisionType");
    };
    const loadReferenceValues = (
      organisationId: number,
      organisationIndexCategories: IOrganisationIndexCategory[],
      caseDecisionTypes: IDocumentType[],
      organisationCaseDecisionTypes: IDocumentType[]
    ) => {
      _store.update({
        caseDecisionTypes: caseDecisionTypes.filter(
          (item) =>
            !organisationCaseDecisionTypes.map((el) => el.id).includes(item.id)
        ),
        organisationIndexCategories,
        organisationId,
        organisationCaseDecisionTypes
      });
      loader.stop("organisationCaseDecisionType");
    };
    const getCaseDecisionTypeIndexCategories = async (
      caseDecisionTypeId: number
    ) => {
      const { organisationId } =
        _store.getState<TOrganisationCaseDecisionTypesPresenter>();
      let caseDecisionTypeIndexCategories = await _application.container
        .resolve<GetCaseDecisionTypeIndexCategoriesInteractor>(
          "getCaseDecisionTypeIndexCategories"
        )
        .execute(caseDecisionTypeId, organisationId);
      _store.update({ caseDecisionTypeIndexCategories });
    };
    const addCaseDecisionTypeIndexCategories = async (items: any) => {
      const { currentCaseDecisionTypeId, organisationId } =
        _store.getState<TOrganisationCaseDecisionTypesPresenter>();
      var itemList = items.fields?.map((item) =>
        DocumentTypeIndexCategory(item)
      );
      currentCaseDecisionTypeId &&
        (await _application.container
          .resolve<AddCaseDecisionTypeIndexCategoriesInteractor>(
            "addCaseDecisionTypeIndexCategories"
          )
          .execute(itemList, currentCaseDecisionTypeId, organisationId));
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
    };
    const onChangeAddElementsModalVisible = async (
      value: boolean,
      itemId?: number
    ) => {
      _store.update({
        modalVisible: value
      });
      if (itemId) {
        await getCaseDecisionTypeIndexCategories(itemId);

        _store.update({ currentCaseDecisionTypeId: itemId });
      } else {
        _store.update({
          caseDecisionTypeIndexCategories: [],
          currentCaseDecisionTypeId: undefined
        });
      }
    };
    const onChangeAddTypesToOrganisationModalVisible = async (
      value: boolean
    ) => {
      _store.update({
        assignTypesToOrganisationModalVisible: value
      });
    };

    const addCaseDecisionTypeToOrganisation = async (item: IDocumentType) => {
      loader.start("organisationCaseDecisionType");
      const { organisationId } =
        _store.getState<TOrganisationCaseDecisionTypesPresenter>();

      item.visibilityLevels = item.visibilityLevels.map((elem) => {
        return {
          status: elem.status,
          valid: true,
          visibilityLevel: elem.visibilityLevel,
          organisationId: organisationId,
          documentTypeId: item.id
        } as IVisibilityLevel;
      });
      await _application.container
        .resolve<AddOrganisationCaseDecisionTypesInteractor>(
          "addOrganisationCaseDecisionTypes"
        )
        .execute(item, organisationId);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
      _store.update({
        caseDecisionTypesForOrganisation: []
      });
      getCaseDecisionTypesForOrganisation();
      loader.stop("organisationCaseDecisionType");
      return true;
    };
    const removeOrganisationCaseLawDocumentType = async (id: number) => {
      const { organisationId } =
        _store.getState<TOrganisationCaseDecisionTypesPresenter>();
      await _application.container
        .resolve<RemoveOrganisationCaseDecisionTypeInteractor>(
          "removeOrganisationCaseDecisionType"
        )
        .execute(organisationId, id);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
      getCaseDecisionTypesForOrganisation();
      return true;
    };
    const setOrganisationCaseDecisionTypeData = async (id: number) => {
      const { organisationId, codeList } =
        _store.getState<TOrganisationCaseDecisionTypesPresenter>();
      if (!codeList.includes(id)) {
        codeList.push(id);
      } else {
        codeList.splice(codeList.indexOf(id), 1);
      }
      _store.update({ codeList, organisationId });
    };

    const getCaseDecisionTypesForOrganisation = async () => {
      let organisationId =
        _store.getState<TOrganisationCaseDecisionTypesPresenter>()
          .organisationId;
      loader.start("organisationCaseDecisionType");
      let caseDecisionTypesForOrganisation = await _application.container
        .resolve<GetOrganisationCaseDecisionTypesInteractor>(
          "getOrganisationCaseDecisionTypes"
        )
        .execute(organisationId);
      let caseDecisionTypes = await _application.container
        .resolve<GetAllCaseDecisionTypesInteractor>("getAllCaseDecisionTypes")
        .execute();

      loadOrganisationCaseDecisionTypes(
        caseDecisionTypesForOrganisation,
        caseDecisionTypes
      );
    };
    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      loadReferenceValues,
      onChangeAddElementsModalVisible,
      onChangeAddTypesToOrganisationModalVisible,
      addCaseDecisionTypeToOrganisation,
      getCaseDecisionTypesForOrganisation,
      setOrganisationCaseDecisionTypeData,
      loadOrganisationCaseDecisionTypes,
      getCaseDecisionTypeIndexCategories,
      addCaseDecisionTypeIndexCategories,
      removeOrganisationCaseLawDocumentType
    };
  },
  defaultState
);

export default OrganisationCaseDecisionTypesPresenter;
